/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as service from '@/services/superadmin/role-templates.service';
import type {
  RoleTemplateDto,
  PermissionDto,
  OrganizationSummaryDto,
  CreateRoleTemplateRequest,
  UpdateRoleTemplateRequest,
  BroadcastResultDto,
} from '@/services/superadmin/role-templates.service';

// ─── Query Keys ─────────────────────────────────────────────────────────────

export const templateKeys = {
  all: ['role-templates'] as const,
  lists: (moduleScope?: string) => [...templateKeys.all, 'list', moduleScope ?? 'all'] as const,
  detail: (id: string) => [...templateKeys.all, 'detail', id] as const,
  permissions: () => [...templateKeys.all, 'permissions'] as const,
  targets: () => [...templateKeys.all, 'broadcast-targets'] as const,
};

// ─── Queries ────────────────────────────────────────────────────────────────

export function useRoleTemplates(moduleScope?: string) {
  return useQuery<RoleTemplateDto[]>({
    queryKey: templateKeys.lists(moduleScope),
    queryFn: () => service.listTemplates(moduleScope),
    staleTime: 5 * 60 * 1000,
  });
}

export function useRoleTemplate(id: string) {
  return useQuery<RoleTemplateDto>({
    queryKey: templateKeys.detail(id),
    queryFn: () => service.getTemplate(id),
    enabled: !!id,
  });
}

export function useTemplatePermissions() {
  return useQuery<PermissionDto[]>({
    queryKey: templateKeys.permissions(),
    queryFn: service.listPermissions,
    staleTime: 10 * 60 * 1000,
  });
}

export function useBroadcastTargets() {
  return useQuery<OrganizationSummaryDto[]>({
    queryKey: templateKeys.targets(),
    queryFn: service.listBroadcastTargets,
    staleTime: 5 * 60 * 1000,
  });
}

// ─── Mutations ──────────────────────────────────────────────────────────────

export function useCreateTemplate() {
  const qc = useQueryClient();
  return useMutation<RoleTemplateDto, Error, CreateRoleTemplateRequest>({
    mutationFn: service.createTemplate,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: templateKeys.all });
    },
  });
}

export function useUpdateTemplate() {
  const qc = useQueryClient();
  return useMutation<RoleTemplateDto, Error, { id: string; data: UpdateRoleTemplateRequest }>({
    mutationFn: ({ id, data }) => service.updateTemplate(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: templateKeys.all });
    },
  });
}

export function useDeleteTemplate() {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: service.deleteTemplate,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: templateKeys.all });
    },
  });
}

export function useBroadcastTemplate() {
  const qc = useQueryClient();
  return useMutation<BroadcastResultDto, Error, string>({
    mutationFn: service.broadcastTemplate,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: templateKeys.all });
    },
  });
}
